/** Global keyboard shortcuts, clipboard paste and drag-and-drop. */
import { batch } from '@preact/signals';
import { deleteRoi, duplicateRoi, loadBlob, nudgeRoi } from '../app/actions';
import { captureState, freezeFrame } from '../app/capture';
import { loadSessionBlob, saveSession } from '../app/session';
import { display, hover, notify, redo, selectedId, tool, undo, type SlotId, type ToolId } from '../app/store';
import { calibrationRequest, polygonDraft, snapPixels, viewportApi } from '../view/viewState';
import { spaceDown } from '../view/Viewport';
import { inspectorFreeze } from './panels/InspectorPanel';
import { openFilePicker } from './panels/SourcesPanel';
import { embedImages } from './panels/ExportPanel';
import { openPanel, togglePanel } from './WindowManager';
import { addRoi, nextRoiDefaults } from '../app/actions';
import type { PolygonRoi } from '../core/roi';

const TOOL_KEYS: Record<string, ToolId> = {
  v: 'select',
  h: 'pan',
  l: 'line',
  r: 'rect',
  e: 'ellipse',
  p: 'polygon',
  o: 'point',
  c: 'calibrate',
};

function typing(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
}

let lastShift = false;

function closePolygon() {
  const d = polygonDraft.value;
  polygonDraft.value = null;
  if (d && d.pts.length >= 3) {
    addRoi({ ...nextRoiDefaults('polygon'), kind: 'polygon', pts: d.pts } as PolygonRoi);
    tool.value = 'select';
  }
}

export async function pasteFromClipboard(slot: SlotId) {
  try {
    const items = await navigator.clipboard.read();
    for (const it of items) {
      const type = it.types.find((t) => t.startsWith('image/'));
      if (type) {
        await loadBlob(slot, await it.getType(type), `Clipboard ${new Date().toLocaleTimeString()}`, 'paste');
        return;
      }
    }
    notify('warn', 'The clipboard does not contain an image');
  } catch {
    notify('info', 'Press ⌘/Ctrl + V to paste (the browser blocked direct clipboard access)');
  }
}

export function installGlobalInput(): () => void {
  const keydown = (e: KeyboardEvent) => {
    lastShift = e.shiftKey;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && !e.altKey) {
      const k = e.key.toLowerCase();
      if (k === 'o') {
        e.preventDefault();
        openFilePicker(e.shiftKey ? 'B' : 'A');
        return;
      }
      if (k === 's') {
        e.preventDefault();
        saveSession(embedImages.value);
        return;
      }
      if (k === 'e') {
        e.preventDefault();
        openPanel('export');
        return;
      }
      if (typing(e)) return;
      if (k === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (k === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (k === 'd' && selectedId.value) {
        e.preventDefault();
        duplicateRoi(selectedId.value);
        return;
      }
      return;
    }
    if (typing(e)) return;
    const k = e.key;
    if (k === ' ') {
      spaceDown.current = true;
      e.preventDefault();
      return;
    }
    if (k === 'Escape') {
      if (calibrationRequest.value) {
        calibrationRequest.value = null;
        return;
      }
      batch(() => {
        if (polygonDraft.value) polygonDraft.value = null;
        else if (tool.value !== 'select') tool.value = 'select';
        else selectedId.value = null;
      });
      return;
    }
    if (k === 'Enter' && polygonDraft.value) {
      closePolygon();
      return;
    }
    if ((k === 'Delete' || k === 'Backspace') && !e.repeat) {
      const d = polygonDraft.value;
      if (d) {
        polygonDraft.value = d.pts.length > 1 ? { ...d, pts: d.pts.slice(0, -1) } : null;
        return;
      }
      if (selectedId.value) {
        e.preventDefault();
        deleteRoi(selectedId.value);
      }
      return;
    }
    if (k.startsWith('Arrow') && selectedId.value) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : e.altKey ? 0.1 : 1;
      const dx = k === 'ArrowLeft' ? -step : k === 'ArrowRight' ? step : 0;
      const dy = k === 'ArrowUp' ? -step : k === 'ArrowDown' ? step : 0;
      nudgeRoi(selectedId.value, dx, dy, !e.repeat);
      return;
    }
    const lk = k.toLowerCase();
    if (TOOL_KEYS[lk] && !e.shiftKey) {
      tool.value = TOOL_KEYS[lk];
      if (lk !== 'p') polygonDraft.value = null;
      return;
    }
    switch (k) {
      case 'f':
      case 'F':
        viewportApi.current?.fit();
        return;
      case '1':
        viewportApi.current?.oneToOne();
        return;
      case '+':
      case '=':
        viewportApi.current?.zoomBy(1.5);
        return;
      case '-':
      case '_':
        viewportApi.current?.zoomBy(1 / 1.5);
        return;
      case 'g':
      case 'G':
        if (captureState.value.active) freezeFrame(e.shiftKey ? 'B' : 'A');
        return;
      case 'i':
      case 'I':
        inspectorFreeze.value = inspectorFreeze.value ? null : hover.value ? { ...hover.value } : null;
        return;
      case 's':
      case 'S':
        snapPixels.value = !snapPixels.value;
        return;
      case 'm':
      case 'M': {
        const order = ['color', 'gray', 'false'] as const;
        const i = order.indexOf(display.value.mode);
        display.value = { ...display.value, mode: order[(i + 1) % 3] };
        return;
      }
      case '?':
        togglePanel('help');
        return;
    }
  };
  const keyup = (e: KeyboardEvent) => {
    if (e.key === ' ') spaceDown.current = false;
  };
  const paste = (e: ClipboardEvent) => {
    if (typing(e) && !Array.from(e.clipboardData?.items ?? []).some((i) => i.type.startsWith('image/'))) return;
    const items = Array.from(e.clipboardData?.items ?? []);
    const file = items.find((i) => i.kind === 'file')?.getAsFile();
    if (!file) return;
    e.preventDefault();
    const slot: SlotId = lastShift ? 'B' : 'A';
    loadBlob(slot, file, file.name && file.name !== 'image.png' ? file.name : `Clipboard ${new Date().toLocaleTimeString()}`, 'paste');
  };
  let depth = 0;
  const overlay = () => document.body.classList.toggle('dropping', depth > 0);
  const dragenter = (e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    depth++;
    overlay();
  };
  const dragleave = () => {
    depth = Math.max(0, depth - 1);
    overlay();
  };
  const dragover = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
  };
  const drop = (e: DragEvent) => {
    depth = 0;
    overlay();
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (!files.length) return;
    e.preventDefault();
    const session = files.find((f) => f.name.endsWith('.aperto'));
    if (session) {
      loadSessionBlob(session);
      return;
    }
    loadBlob('A', files[0], files[0].name);
    if (files[1]) loadBlob('B', files[1], files[1].name);
  };
  window.addEventListener('keydown', keydown);
  window.addEventListener('keyup', keyup);
  window.addEventListener('blur', () => (spaceDown.current = false));
  document.addEventListener('paste', paste);
  window.addEventListener('dragenter', dragenter);
  window.addEventListener('dragleave', dragleave);
  window.addEventListener('dragover', dragover);
  window.addEventListener('drop', drop);
  return () => {
    window.removeEventListener('keydown', keydown);
    window.removeEventListener('keyup', keyup);
    document.removeEventListener('paste', paste);
    window.removeEventListener('dragenter', dragenter);
    window.removeEventListener('dragleave', dragleave);
    window.removeEventListener('dragover', dragover);
    window.removeEventListener('drop', drop);
  };
}
